import React, { useEffect, useState, useCallback } from 'react';
import {
  useColorStyle,
  Flex,
  Box,
  Text,
  ButtonBase,
  Icon,
  Image,
  Badge,
  Space,
} from '@tonic-ui/react';
import { DropdownMenu } from './../components';
import { formatDistanceToNow } from 'date-fns';
import { enUS, ja } from 'date-fns/locale';
import { withTranslation } from 'react-i18next';
import { getWSNotifications } from '../ws-notifications';

import success from '../assets/images/notifications/icon-notification-success.svg';
import error from '../assets/images/notifications/icon-notification-error.svg';
import progress from '../assets/images/notifications/icon-notification-progress.svg';
import empty from '../assets/images/notifications/icon-notification-empty.svg';

const NOTIFICATION_ICONS = {
  INPROGRESS: progress,
  COMPLETED: success,
  FAILED: error,
};
const NOTIFICATION_STATUSES_KEYS = {
  INPROGRESS: 'notifications.status.pending',
  COMPLETED: 'notifications.status.success',
  FAILED: 'notifications.status.failed',
};

const NOTIFICATION_SERVICES = {
  workload: 'notifications.services.workload',
};
const LOCALE_MAP = { en: enUS, ja };
const DEFAULT_NOTIFICATIONS_DATA = { notifications: [], count: 0 };
const POLLING_INTERVAL = 15000;

const NOTIFICATION_EMPTY = {
  notificationless: 'notifications.noNotifications',
};

const NOTIFICATION_PROBLEM = {
  mainsMessage: 'notifications.notificationProblem.mainMessage',
  secondaryMessage: 'notifications.notificationProblem.secondaryMessage',
};

// A custom event name that will be created by DSM when it loads
const FETCH_NOTIFICATIONS_EVENT = 'C1C_WS_Notifications';

const extractFilenameFromS3SignedURI = (signedURI) => {
  let filename = 'no-filename';
  try {
    const url = new URL(signedURI);
    const path = decodeURIComponent(url.pathname);
    filename = path.substring(path.lastIndexOf('/') + 1);
  } catch (e) {
    // do nothing
  }

  return filename;
};

/*
	Utility function for fallback in the case that the notification is missing taskType data
		Should be in its own file but was hitting an export bug failure tests
	List of taskTypes can be found here: https://wiki.jarvis.trendmicro.com/display/DS/Notification+Service:+Task+Types
	Try to first find the string key from _taskType
	Fall back to using _type number as the key
		This fallback is used in case notification's taskType column is empty
	To add new taskTypes, please first:
	1. Update wiki article
	2. Add string to string files (localised in English and Japanese)
	3. Add new entry to NOTIFICATION_TYPES_KEYS
	4. Update Switch where the taskType value maps to the numerical key added in #3
*/
const getNotificationType = (_type, _taskType) => {
  const NOTIFICATION_TYPES_KEYS = {
    1: 'notifications.types.exportComputer',
    2: 'notifications.types.sendPolicy',
    3: 'notifications.types.dismissAlert',
    4: 'notifications.types.agentVersionReport',
    5: 'notifications.types.alertReport',
    6: 'notifications.types.antiMalwareReport',
    7: 'notifications.types.attackReport',
    8: 'notifications.types.computerReport',
    9: 'notifications.types.dpiRuleRecommendationReport',
    10: 'notifications.types.firewallReport',
    11: 'notifications.types.forensicComputerAuditReport',
    12: 'notifications.types.integrityMonitoringDetailedChangeReport',
    13: 'notifications.types.integrityMonitoringReport',
    14: 'notifications.types.intrusionPreventionReport',
    15: 'notifications.types.logInspectionDetailedReport',
    16: 'notifications.types.logInspectionReport',
    17: 'notifications.types.recommendationReport',
    18: 'notifications.types.recommendationSummaryReport',
    19: 'notifications.types.securityModuleUsageReport',
    20: 'notifications.types.summaryReport',
    21: 'notifications.types.suspiciousApplicationActivityReport',
    22: 'notifications.types.systemEventReport',
    23: 'notifications.types.userAndContactReport',
    24: 'notifications.types.webReputationReport',
    25: 'notifications.types.exportSystemEvent',
    26: 'notifications.types.exportAntiMalwareIdentifiedFile',
    27: 'notifications.types.exportFirewallEvent',
    28: 'notifications.types.exportLogInspectionEvent',
    29: 'notifications.types.exportDeviceControlEvent',
    30: 'notifications.types.exportApplicationControlEvent',
    31: 'notifications.types.exportIntegrityMonitoringEvent',
    32: 'notifications.types.exportWebReputationEvent',
    33: 'notifications.types.exportAntiMalwareEvent',
    34: 'notifications.types.exportIntrusionPreventionEvent'
  };

  let referenceValue;

  switch (_taskType) {
    case 'Export Computer':
      referenceValue = 1;
      break;
    case 'Send Policy':
      referenceValue = 2;
      break;
    case 'Dismiss Alert':
    // Fallback for rows without updated values
    case 'Dismiss Alerts':
      referenceValue = 3;
      break;
    case 'Agent Version Report':
      referenceValue = 4;
      break;
    case 'Alert Report':
      referenceValue = 5;
      break;
    case 'Anti-Malware Report':
      referenceValue = 6;
      break;
    case 'Attack Report':
      referenceValue = 7;
      break;
    case 'Computer Report':
      referenceValue = 8;
      break;
    case 'DPI Rule Recommendation Report':
      referenceValue = 9;
      break;
    case 'Firewall Report':
      referenceValue = 10;
      break;
    case 'Forensic Computer Audit Report':
      referenceValue = 11;
      break;
    case 'Integrity Monitoring Detailed Change Report':
      referenceValue = 12;
      break;
    case 'Integrity Monitoring Report':
      referenceValue = 13;
      break;
    case 'Intrusion Prevention Report':
      referenceValue = 14;
      break;
    case 'Log Inspection Detailed Report':
      referenceValue = 15;
      break;
    case 'Log Inspection Report':
      referenceValue = 16;
      break;
    case 'Recommendation Report':
      referenceValue = 17;
      break;
    case 'Recommendation Summary Report':
      referenceValue = 18;
      break;
    case 'Security Module Usage Report':
      referenceValue = 19;
      break;
    case 'Summary Report':
      referenceValue = 20;
      break;
    case 'Suspicious Application Activity Report':
      referenceValue = 21;
      break;
    case 'System Event Report':
      referenceValue = 22;
      break;
    case 'User and Contact Report':
      referenceValue = 23;
      break;
    case 'Web Reputation Report':
      referenceValue = 24;
      break;
    case 'Export System Event':
      referenceValue = 25;
      break;
    case 'Export Anti-Malware Identified File':
      referenceValue = 26;
      break;
    case 'Export Firewall Event':
      referenceValue = 27;
      break;
    case 'Export Log Inspection Event':
      referenceValue = 28;
      break;
    case 'Export Device Control Event':
      referenceValue = 29;
      break;
    case 'Export Application Control Event':
      referenceValue = 30
      break;
    case 'Export Integrity Monitoring Event':
      referenceValue = 31
      break;
    case 'Export Web Reputation Event':
      referenceValue = 32
      break;
    case 'Export Anti Malware Event':
      referenceValue = 33
      break;
    case 'Export Intrusion Prevention Event':
      referenceValue = 34
      break;
    default:
      referenceValue = -1;
  }

  if (referenceValue >= 1) {
    return NOTIFICATION_TYPES_KEYS[referenceValue];
  }

  return NOTIFICATION_TYPES_KEYS[_type];
};

export const NotificationProblem = withTranslation()(({ t, apiError }) => {
  const colorMode = 'dark';
  const [colorStyle] = useColorStyle({ colorMode });

  return (
    <Flex alignItems="center" justifyContent="center" width="440px" height="337px">
      <Box width="360px">
        <Text
          textAlign="center"
          weight="600"
          fontSize="20px"
          lineHeight="28px"
          color={colorStyle.color.primary}
        >
          {t(NOTIFICATION_PROBLEM.mainsMessage)}
        </Text>
        <Space height="25px" />
        <Text
          whiteSpace="normal"
          textAlign="center"
          weight="400"
          fontSize="14px"
          lineHeight="20px"
          color={colorStyle.color.tertiary}
        >
          {t(NOTIFICATION_PROBLEM.secondaryMessage, { errorCode: apiError.code })}
        </Text>
      </Box>
    </Flex>
  );
});

export const NoNotifications = withTranslation()(({ t }) => {
  const colorMode = 'dark';
  const [colorStyle] = useColorStyle({ colorMode });

  return (
    <Flex
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      width={'440px'}
      height={'337px'}
    >
      <Box mb="3x">
        <Image alt="alt" src={empty} />
      </Box>
      <Text color={colorStyle.color.tertiary}>{t(NOTIFICATION_EMPTY.notificationless)}</Text>
    </Flex>
  );
});

export const NotificationCenterItem = withTranslation()(
  ({ status, time, type, locale, url, t, taskType }) => {
    const colorMode = 'dark';
    const [colorStyle] = useColorStyle({ colorMode });
    const styleProps = {
      backgroundColor: 'transparent',
      px: '2x',
      py: '2x',
      textAlign: 'left',
      width: '100%',
    };

    const message = t(getNotificationType(type, taskType));

    return (
      <ButtonBase {...styleProps}>
        <Flex columnGap="2x">
          <Box position="relative" width="9x">
            <Image alt="alt" src={NOTIFICATION_ICONS[status]} />
          </Box>
          <Box>
            <Box color={colorStyle.color.primary}>
              <Text size="sm">{message}</Text>
            </Box>
            <Box mb="3x" color={colorStyle.color.primary}>
              <Text lineHeight="xs" size="xs">
                {t(NOTIFICATION_STATUSES_KEYS[status])}
              </Text>
            </Box>
            {time && (
              <Text lineHeight="xs" size="xs" color={colorStyle.color.secondary}>
                {formatDistanceToNow(time, { addSuffix: true, locale: LOCALE_MAP[locale] })}
                {` • ${t(NOTIFICATION_SERVICES.workload)}`}
              </Text>
            )}
          </Box>
          {url && ( // URL means that the file is downloadable
            <Box marginLeft="auto">
              <Box marginLeft="20x">
                <Icon
                  icon="download"
                  width="1.5rem"
                  height="1.5rem"
                  padding="2px"
                  borderRadius="3px"
                  border="1px solid #5E5E5E"
                />
              </Box>
            </Box>
          )}
        </Flex>
      </ButtonBase>
    );
  }
);

const NotificationsDropdownMenu = ({
  isResponsive = false,
  c1Token,
  userURN,
  currentAccount,
  locale,
}) => {
  const colorMode = 'dark';
  const [colorStyle] = useColorStyle({ colorMode });
  const [notificationsData, setNotificationsData] = useState(DEFAULT_NOTIFICATIONS_DATA);
  const [apiError, setApiError] = useState();

  const { region } = currentAccount || {};
  const getNextWSNotifications = useCallback(() => {
    if (!currentAccount?.id || !c1Token || !userURN || !region) return;

    getWSNotifications({
      c1Token,
      customerAccountId: currentAccount.id,
      locale,
      userURN,
      accountRegion: region,
    })
      .then((data = DEFAULT_NOTIFICATIONS_DATA) => {
        setNotificationsData(data);
        setApiError(undefined); //Reset apiError, in case we have already received items.
      })
      .catch((error) => {
        console.error('getWSNotifications():', error);
        setApiError(error);
      });
  }, [currentAccount?.id, c1Token, userURN, region, locale]);

  // start polling when the component is mounted
  useEffect(() => {
    getNextWSNotifications();

    const notificationTimerId = setInterval(getNextWSNotifications, POLLING_INTERVAL);

    const handleFetchNotificationsEvent = (event) => {
      // console.log('Storage event:', event);
      getNextWSNotifications();
    };

    // An event sent by DSM when there is a post to notification service. Used to immediately check for notifications indicating a long-running job has completed.
    window.addEventListener(FETCH_NOTIFICATIONS_EVENT, handleFetchNotificationsEvent);
    return () => {
      window.removeEventListener(FETCH_NOTIFICATIONS_EVENT, handleFetchNotificationsEvent);
      clearInterval(notificationTimerId);
    };
  }, [getNextWSNotifications]);

  const notificationsList = [];

  if (apiError?.code) {
    notificationsList.push({
      label: <NotificationProblem apiError={apiError} />,
      value: undefined,
    });
  } else if (!notificationsData.count) {
    notificationsList.push({ label: <NoNotifications />, value: undefined });
  } else {
    notificationsData.notifications.forEach((notification, index) => {
      const notificationId = `ws-notification-${index}`;
      return notificationsList.push({
        label: (
          <NotificationCenterItem
            {...notification}
            key={index}
            service="workload"
            locale={locale}
            time={1000 * parseInt(notification.timeCreated)}
          />
        ),
        value: notificationId,
        data: notification,
      });
    });
  }

  const onDownloadReport = ({ data }) => {
    if (!data?.url) {
      return;
    }
    window.open(data.url, '_blank');
  };

  return (
    <DropdownMenu
      id="c1c_notification_dropdown"
      isSeparatorVisible
      selectedItem={
        <Badge marginTop="8px" badgeContent={notificationsData.count || null}>
          <Icon marginTop="8px" icon="alert" />
        </Badge>
      }
      menuList={notificationsList}
      menuListProps={{ maxHeight: '400px' }}
      isResponsive={isResponsive}
      menuButtonProps={{ isMenuToggleVisible: false }}
      onChange={onDownloadReport}
    />
  );
};

export default NotificationsDropdownMenu;
